import { Select, SimpleGrid, Stack, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Control, Controller, FieldPath } from 'react-hook-form';

import { PATIENT_SEX_OPTIONS, selectablePatientSex } from '@/utils/patientSex';
import poc from '@/styles/pocScope.module.scss';

import {
  DEVICE_TYPE_OPTIONS,
  MANUFACTURER_OPTIONS
} from '../TransmissionReportPage/DeviceInfo/DeviceEditModal';

import styles from './CreatePatientModal.module.scss';

import type { CreatePatientFormValues } from './CreatePatient';

/**
 * Add Patient fields on plain Mantine inputs, so they take the POC look from `.pocScope`
 * (36px, 8px radius, 13px text, light borders). The shared react-select / react-datepicker
 * wrappers keep the old portal styling and don't belong in this modal.
 */

type Control_ = Control<CreatePatientFormValues>;

// Dropdowns and calendars portal out of the modal, so they carry the POC scope themselves.
export const POC_DROPDOWN = {
  withinPortal: true,
  zIndex: 1300,
  classNames: { dropdown: poc.pocScope }
};
const COMBO = POC_DROPDOWN;

const toData = (options: { label: unknown; value: unknown }[]) =>
  options.map((option) => ({ value: String(option.value), label: String(option.label) }));

const SEX_DATA = toData(PATIENT_SEX_OPTIONS.filter((option) => option.value));
const VENDOR_DATA = toData(MANUFACTURER_OPTIONS);
const TYPE_DATA = toData(DEVICE_TYPE_OPTIONS);

/** Typed dates: MM/DD/YYYY (no dayjs customParseFormat plugin needed). */
const parseUsDate = (input: string): Date | null => {
  const match = input.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const date = new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
  return Number.isNaN(date.getTime()) ? null : date;
};

function TextField({
  control,
  name,
  label,
  required,
  placeholder
}: {
  control: Control_;
  name: FieldPath<CreatePatientFormValues>;
  label: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextInput
          label={label}
          withAsterisk={required}
          placeholder={placeholder}
          value={(field.value as string) ?? ''}
          onChange={(event) => field.onChange(event.currentTarget.value)}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}

function DateField({
  control,
  name,
  label,
  maxToday
}: {
  control: Control_;
  name: FieldPath<CreatePatientFormValues>;
  label: string;
  maxToday?: boolean;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <DateInput
          label={label}
          withAsterisk
          placeholder="MM/DD/YYYY"
          valueFormat="MM/DD/YYYY"
          dateParser={parseUsDate}
          maxDate={maxToday ? new Date() : undefined}
          rightSection={<IconCalendar size={14} color="#9aa1b4" />}
          rightSectionPointerEvents="none"
          popoverProps={COMBO}
          value={field.value ? dayjs(field.value as string).toDate() : null}
          onChange={(date) => field.onChange(date ? dayjs(date).format('YYYY-MM-DD') : '')}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}

function SelectField({
  control,
  name,
  label,
  data,
  placeholder
}: {
  control: Control_;
  name: FieldPath<CreatePatientFormValues>;
  label: string;
  data: { value: string; label: string }[];
  placeholder: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Select
          label={label}
          withAsterisk
          placeholder={placeholder}
          data={data}
          value={
            name === 'sex'
              ? selectablePatientSex(field.value as string) || null
              : (field.value as string) || null
          }
          onChange={(value) =>
            field.onChange(name === 'sex' ? selectablePatientSex(value) : (value ?? ''))
          }
          onBlur={field.onBlur}
          allowDeselect={false}
          comboboxProps={COMBO}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}

export function PatientFields({
  control,
  mrnLabel,
  mrnRequired
}: {
  control: Control_;
  mrnLabel: string;
  mrnRequired: boolean;
}) {
  return (
    <Stack gap="sm">
      <div className={styles.sectionTitle}>Patient Information</div>
      <SimpleGrid cols={3} spacing="md" verticalSpacing="sm">
        <TextField control={control} name="firstName" label="First Name" required />
        <TextField control={control} name="middleName" label="Middle Name (optional)" />
        <TextField control={control} name="lastName" label="Last Name" required />
        <DateField control={control} name="dob" label="Date of Birth" maxToday />
        <SelectField
          control={control}
          name="sex"
          label="Sex"
          data={SEX_DATA}
          placeholder="Select"
        />
        {/* Same label as the patient page (MRN, or MPI); required on EHR integrated accounts. */}
        <TextField control={control} name="mrn" label={mrnLabel} required={mrnRequired} />
      </SimpleGrid>
    </Stack>
  );
}

export function DeviceFields({ control, vendor }: { control: Control_; vendor: string }) {
  const biotronik = vendor?.toLowerCase() === 'biotronik';
  return (
    <Stack gap="sm">
      <div className={styles.sectionTitle}>Device Information</div>
      <SimpleGrid cols={3} spacing="md" verticalSpacing="sm">
        <SelectField
          control={control}
          name="vendor"
          label="Manufacturer"
          data={VENDOR_DATA}
          placeholder="Select manufacturer"
        />
        <SelectField
          control={control}
          name="type"
          label="Type"
          data={TYPE_DATA}
          placeholder="Select type"
        />
        <TextField control={control} name="serialNumber" label="Serial Number" required />
        <TextField control={control} name="modelName" label="Model Name" required={biotronik} />
        <TextField
          control={control}
          name="modelNumber"
          label="Model Number"
          required={!biotronik}
        />
        <DateField control={control} name="implantDate" label="Implant Date" maxToday />
      </SimpleGrid>
    </Stack>
  );
}
